'use client';

import { format, addDays, startOfDay, isAfter, isBefore } from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { FieldErrorMessage } from '@/components/ui/FormFields/FieldErrorMessage';
import { Label } from '@/components/ui/FormFields/Label';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import { changedFieldClassName, useIsChangedField } from '@/components/shared/FormChangeHighlightContext';
import { useRequiredField } from '@/components/shared/FormRequiredFieldsContext';

type DateFieldProps = {
  id: string;
  label?: string;
  description?: string;
  error?: string;
  value?: Date;
  defaultValue?: Date; // Default date selection from parent component
  fieldName?: string;
  required?: boolean;
  disabled?: boolean;
  onChange?: (date: Date | undefined) => void;
  containerClassName?: string;
  labelClassName?: string;
  className?: string;
  // Date restriction props
  minDaysFromToday?: number; // Minimum days from today (e.g., 6 = earliest date is 6 days from today)
  disableMinDateFromToday?: boolean; // If true, allows selecting past dates by ignoring minDaysFromToday
  maxDaysFromToday?: number; // Maximum days from today (e.g., 30 = latest date is 30 days from today)
  minDate?: Date; // Absolute minimum date
  maxDate?: Date; // Absolute maximum date
  disableWeekends?: boolean; // Disable Saturday and Sunday
  disabledMatcher?: (date: Date) => boolean; // Custom disable function
  disabledDatesHint?: string; // Helper text shown below field
};

const DateField = ({
  id,
  label,
  description,
  error,
  value,
  defaultValue,
  fieldName,
  onChange,
  required,
  disabled,
  containerClassName = 'space-y-2',
  labelClassName = 'text-primary text-xs',
  className,
  minDaysFromToday = 0,
  disableMinDateFromToday = false,
  maxDaysFromToday,
  minDate,
  maxDate,
  disableWeekends,
  disabledMatcher,
  disabledDatesHint,
  ...props
}: DateFieldProps) => {
  const t = useTranslations('common.placeholders');
  const isRequired = useRequiredField(fieldName, required);
  const isChanged = useIsChangedField(fieldName);
  const getDateTime = (date: Date | undefined): number | null => {
    if (!date) {
      return null;
    }

    const time = new Date(date).getTime();

    return Number.isNaN(time) ? null : time;
  };

  const [isOpen, setIsOpen] = useState(false);
  const calendarRootRef = useRef<HTMLDivElement>(null);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const onChangeRef = useRef(onChange);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(() => {
    // Initialize with defaultValue or today's date if no defaultValue is provided
    return defaultValue; // ?? new Date();
  });
  const syncedDefaultValueTimeRef = useRef<number | null>(null);
  const valueTime = getDateTime(value);
  const defaultValueTime = getDateTime(defaultValue);

  const updateSelectedDate = useCallback((date: Date | undefined) => {
    setSelectedDate((currentDate) => {
      if (currentDate?.getTime() === date?.getTime()) {
        return currentDate;
      }

      return date;
    });
  }, []);

  // Calculate disabled dates based on props
  const getDisabledDates = (date: Date): boolean => {
    const today = startOfDay(new Date());
    const checkDate = startOfDay(date);

    // Check minDaysFromToday
    if (!disableMinDateFromToday && minDaysFromToday !== undefined) {
      const minAllowedDate = addDays(today, minDaysFromToday);

      if (isBefore(checkDate, minAllowedDate)) {
        return true;
      }
    }

    // Check maxDaysFromToday
    if (maxDaysFromToday !== undefined) {
      const maxAllowedDate = addDays(today, maxDaysFromToday);

      if (isAfter(checkDate, maxAllowedDate)) {
        return true;
      }
    }

    // Check absolute minDate
    if (minDate && isBefore(checkDate, startOfDay(minDate))) {
      return true;
    }

    // Check absolute maxDate
    if (maxDate && isAfter(checkDate, startOfDay(maxDate))) {
      return true;
    }

    // Check weekends
    if (disableWeekends) {
      const dayOfWeek = date.getDay();

      if (dayOfWeek === 0 || dayOfWeek === 6) {
        return true;
      }
    }

    // Check custom matcher
    if (disabledMatcher?.(date)) {
      return true;
    }

    return false;
  };

  const handleDateChange = (date: Date | undefined) => {
    updateSelectedDate(date);

    onChange?.(date);
    setIsOpen(false);
  };

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (calendarRootRef.current?.contains(event.target as Node)) {
        return;
      }

      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerButtonRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (valueTime != null) {
      updateSelectedDate(new Date(valueTime));

      return;
    }

    if (defaultValueTime != null) {
      const nextDefaultValue = new Date(defaultValueTime);

      updateSelectedDate(nextDefaultValue);

      if (syncedDefaultValueTimeRef.current !== defaultValueTime) {
        syncedDefaultValueTimeRef.current = defaultValueTime;
        onChangeRef.current?.(nextDefaultValue); // sync with RHF
      }

      return;
    }

    syncedDefaultValueTimeRef.current = null;
    updateSelectedDate(undefined);
  }, [defaultValueTime, updateSelectedDate, valueTime]);

  return (
    <div className={containerClassName}>
      {label && (
        <div className="flex flex-col space-y-2">
          <Label
            htmlFor={id}
            required={isRequired}
            className={cn('text-primary text-xs font-semibold', labelClassName)}
          >
            {label}
          </Label>
          {description && <span className="text-gray800 text-xs">{description}</span>}
        </div>
      )}
      <div ref={calendarRootRef} className="relative">
        <button
          ref={triggerButtonRef}
          aria-controls={`${id}-popover-content`}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          data-state={isOpen ? 'open' : 'closed'}
          disabled={disabled}
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          className={cn(
            'w-full flex items-center justify-between h-8 px-3 border border-tablesDivider rounded-sm bg-inputBackground text-left font-normal text-sm',
            selectedDate ? 'text-gray900' : 'text-gray600',
            'disabled:cursor-not-allowed disabled:opacity-50',
            isChanged && changedFieldClassName,
            className
          )}
        >
          {selectedDate ? format(selectedDate, 'dd-MM-yyyy') : <span>{t('datePicker')}</span>}
          <CalendarIcon className="mr-2 h-4 w-4" />
        </button>

        {isOpen && (
          <div
            id={`${id}-popover-content`}
            // biome-ignore lint/a11y/useSemanticElements: This is a custom anchored date-picker popover, not a modal dialog element.
            role="dialog"
            className="absolute right-0 top-full z-[90] mt-1 w-[380px] p-0 border border-gray-200 shadow-xl"
          >
            <div className="bg-white rounded-lg">
              <DayPicker
                mode="single"
                selected={selectedDate}
                defaultMonth={selectedDate || new Date()}
                onSelect={handleDateChange}
                disabled={getDisabledDates}
                captionLayout="dropdown-buttons"
                fromYear={1900}
                toYear={2100}
                className="rdp-root"
                showOutsideDays={false}
                components={{
                  IconLeft: () => <ChevronLeft className="h-4 w-4" />,
                  IconRight: () => <ChevronRight className="h-4 w-4" />,
                }}
                classNames={{
                  root: 'p-4',
                  months: 'flex flex-col',
                  month: 'w-full',
                  caption: 'flex items-center justify-between mb-4',
                  caption_label: 'hidden',
                  vhidden: 'sr-only',
                  dropdown_icon: 'pointer-events-none absolute right-0 text-gray900',
                  nav: 'flex items-center',
                  nav_button:
                    'inline-flex items-center justify-center rounded-md text-gray-500 h-8 w-8 hover:bg-gray-50 hover:text-gray900 transition-all',
                  nav_button_previous: 'text-primary',
                  nav_button_next: 'text-primary',
                  table: 'w-full',
                  head_row: 'grid grid-cols-7 mb-1',
                  head_cell: 'text-primary text-xs font-medium text-center py-2',
                  row: 'grid grid-cols-7',
                  cell: 'relative p-0.5 text-center',
                  day: 'h-10 w-full inline-flex items-center justify-center text-sm rounded-lg hover:bg-gray-50 transition-colors cursor-pointer',
                  day_today: 'bg-blue-50 text-lightPrimary rounded-xl font-semibold',
                  day_outside: 'text-gray-300',
                  day_selected: 'bg-primary rounded-xl text-white font-semibold hover:bg-primary/90',
                  day_disabled: 'text-gray-300 opacity-50 hover:bg-transparent cursor-not-allowed',
                  day_hidden: 'invisible',
                }}
                {...props}
              />
            </div>
          </div>
        )}
      </div>
      {error ? (
        <FieldErrorMessage message={error} />
      ) : disabledDatesHint ? (
        <span className="mt-1 block min-h-[18px] text-xs leading-[18px] text-gray600">{disabledDatesHint}</span>
      ) : (
        <FieldErrorMessage />
      )}
    </div>
  );
};

export { DateField };
